import React, { useState, useEffect } from 'react';
import { toolboxService } from '../services/api';
import Pagination from '../components/Pagination';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';

const ConversationMessagesTool = () => {
  const { currentUser } = useAuth();
  const [clid, setClid] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [pagination, setPagination] = useState(null);

  // Check if current user is admin
  const isAdmin = currentUser?.username === 'admin';

  const fetchMessages = async () => {
    setLoading(true);
    setError('');

    try {
      const result = await toolboxService.listConversationMessages({
        clid: clid || undefined,
        page,
        per_page: perPage
      });

      if (result.success) {
        setData(result.data);
        setPagination(result.pagination);
      } else {
        setError(result.error || 'Failed to fetch conversation messages');
        setData(null);
        setPagination(null);
      }
    } catch (err) {
      setError(err.message || 'An error occurred while fetching conversation messages');
      setData(null);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch on mount
  useEffect(() => {
    fetchMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-fetch when page or perPage changes (pagination)
  // Filter changes (clid) require manual search
  useEffect(() => {
    if (page > 1 || perPage !== 50) {
      fetchMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage]);

  const handleSearch = () => {
    setPage(1);
    fetchMessages();
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this conversation message? This action cannot be undone.')) {
      return;
    }

    try {
      const result = await toolboxService.deleteConversationMessage(messageId);
      if (result.success) {
        // Refresh the messages list
        fetchMessages();
      } else {
        setError(result.error || 'Failed to delete conversation message');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while deleting the conversation message');
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '(No content)';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const renderTable = () => {
    return (
      <div className="overflow-x-auto border border-gray-300 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conversation ID</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CLID</th>
              {/* <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">From</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">To</th> */}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
              {/* <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th> */}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Content</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Media</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
              {isAdmin && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((message) => (
              <tr key={message.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-900">{message.id}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{message.conversation_id}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{message.clid}</td>
                {/* <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{message.from_number}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{message.to_number}</td> */}
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{message.role || 'N/A'}</td>
                {/* <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{message.message_type}</td> */}
                <td className="px-4 py-3 text-sm text-gray-800 max-w-xs">
                  <div className="truncate" title={message.message_content}>
                    {truncateText(message.message_content, 80)}
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm">
                  {message.media_url ? (
                    <a href={message.media_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      View
                    </a>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{formatTimestamp(message.timestamp)}</td>
                {isAdmin && (
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    <button
                      onClick={() => handleDeleteMessage(message.id)}
                      className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                      title="Delete message"
                    >
                      Delete
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Conversation Messages Tool</h1>
          <p className="text-gray-600">List and filter conversation messages with pagination</p>
        </div>

        {/* Filters */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Clinic ID (CLID)
              </label>
              <input
                type="text"
                value={clid}
                onChange={(e) => setClid(e.target.value)}
                placeholder="Filter by CLID (optional)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Per Page
              </label>
              <select
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
            </div>
          </div>
          <button
            onClick={handleSearch}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Loading...' : 'Search'}
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading conversation messages...</p>
          </div>
        )}

        {/* Results */}
        {!loading && data && (
          <>
            {pagination && (
              <div className="mb-4 text-sm text-gray-600">
                Showing {((pagination.page - 1) * pagination.per_page) + 1} to{' '}
                {Math.min(pagination.page * pagination.per_page, pagination.total)} of{' '}
                {pagination.total} results
              </div>
            )}

            {data.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No conversation messages found.
              </div>
            ) : (
              <>
                {renderTable()}

                {pagination && pagination.total_pages > 1 && (
                  <div className="mt-6">
                    <Pagination
                      currentPage={pagination.page}
                      totalPages={pagination.total_pages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ConversationMessagesTool;

